import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import ApiHelper from '../../../../helpers/apiHelper';
import HourHelper from '../../../../helpers/hourHelper';
import ObjectHelper from '../../../../helpers/objectHelper';
import TimesheetHelper from '../../../../helpers/timesheetHelper';
import WindowHelper from '../../../../helpers/windowHelper';
import {Day} from '../../../../typescript/dateTypes';
import {StoreSettings, StoreTimesheetEntry} from '../../../../typescript/storeTypes';
import {MultiEdit, NavigationEvent} from '../../../../typescript/timesheetTypes';
import TimesheetCodeDayAddHoursComment from './TimesheetCodeDayAddHoursComment';


type Props = {
    entry: StoreTimesheetEntry,
    day: Day,
    isWeekend: boolean,
    isReadOnly: boolean,
    isVacationOrIllness: boolean,
    isCompleted: boolean,
    expanded: boolean,
    settings: StoreSettings,
    onEntryChange: (entry: StoreTimesheetEntry, day: Day) => void,
    onEntryExpand: (fromEntry?: boolean) => void,

    visibleIndex: number,
    dayIndex: number,
    onNavigateEntry: (event: NavigationEvent, dayIndex?: number, day?: Day, level?: number) => void,

    activeRowLevel: number,
    isActive: boolean,
    setActiveEntryRequested: boolean,

    multiSelected: boolean,
    multiSelectEntryPoint: boolean,
    multiSelectLeftBorder: boolean,
    multiSelectRightBorder: boolean,

    commentMandatory: boolean,

    isTsCodeActive: boolean,
    tsCodeId: number,

    onMultiEditSubmit: (multiEdit: MultiEdit) => void,
    onMultiEditCancel: () => void,
    resetToDefaultEntry: () => void
};

export const TimesheetCodeDay = (props:Props) => {                    
    const [entry, setEntry] = useState<StoreTimesheetEntry>({...props.entry});
    const [normalHours, setNormalHours] = useState<string>(props.entry.normalHours ? props.entry.normalHours.toString() : '');
    const [extraHours, setExtraHours] = useState<string>(props.entry.extraHours ? props.entry.extraHours.toString() : '');
    const [standByHours, setStandByHours] = useState<string>(props.entry.standByHours ? props.entry.standByHours.toString() : '');
    const [interventionHours, setInterventionHours] = useState<string>(props.entry.interventionHours ? props.entry.interventionHours.toString() : '');
    const [changeRequested, setChangeRequested] = useState<boolean>(false);
    const [commentVisible, setCommentVisible] = useState<boolean>(false);
    const [scrollIntoView, setScrollIntoView] = useState<boolean>(false);
    
    const setters: Record<string, Dispatch<SetStateAction<string>>> = {
        normalHours: setNormalHours,
        extraHours: setExtraHours,
        standByHours: setStandByHours,
        interventionHours: setInterventionHours,
    };

    let dayHoursContainer: HTMLElement | undefined;
    let normalHoursInput: HTMLInputElement | undefined;
    let normalHoursExpandedInput: HTMLInputElement | undefined;
    let extraHoursExpandedInput: HTMLInputElement | undefined;
    let standByHoursExpandedInput: HTMLInputElement | undefined;
    let interventionHoursExpandedInput: HTMLInputElement | undefined;

    const onCommentAdd = (): void => {
        if (props.isCompleted || props.isReadOnly || props.commentMandatory) return;

        if (commentVisible) {
            onCommentCancel();
        } else {
            setCommentVisible(true);
        }
    };

    const onCommentCancel = (): void => {
        setCommentVisible(false);
        selectActiveInputLevel();
    };

    const onEntryExpand = (): void => {
        props.onEntryExpand(true);
    };

    const onEntryChange = (entry: StoreTimesheetEntry): void => {
        if (changeRequested) return;

        if (!TimesheetHelper.changeInEntries(entry, props.entry)) return;

        setChangeRequested(true);
        props.onEntryChange(entry, props.day);
    };

    const onEntryChangeDebounceRef = useRef(ApiHelper.debounce(onEntryChange, 1000));
    
    // Update the debounced function when dependencies change, but keep the same reference
    useEffect(() => {
        onEntryChangeDebounceRef.current.cancel();
        onEntryChangeDebounceRef.current = ApiHelper.debounce(onEntryChange, 1000);
        // eslint-disable-next-line @eslint-react/exhaustive-deps -- onEntryChange wordt elke render opnieuw aangemaakt; als dependency zou de debounce bij elke toetsaanslag opnieuw opgebouwd worden
    }, [props.onEntryChange, props.day]);
    const onNavigateEntry = (event: NavigationEvent & { currentTarget: HTMLInputElement } | React.MouseEvent<HTMLInputElement, MouseEvent>): void => {
        let level = 0;
        setScrollIntoView(!(event.type === 'click' || event.type.startsWith('mouse')));
        
        switch (event.currentTarget) {
            case normalHoursInput:
                level = 1;
                break;
            case normalHoursExpandedInput:
                level = 2;
                break;
            case extraHoursExpandedInput:
                level = 3;
                break;
            case standByHoursExpandedInput:
                level = 4;
                break;
            case interventionHoursExpandedInput:
                level = 5;
                break;
        }

        // This is here because of an issue in Edge. (SetState Batching messes up UI rendering (i think))
        if ((event as NavigationEvent).key === ' ' && event.type === 'keydown'  && level > 1 && normalHoursInput) normalHoursInput.select();

        props.onNavigateEntry(event as NavigationEvent, props.dayIndex, props.day, level);
    };

    const onChange = (event: React.SyntheticEvent<HTMLInputElement> & { currentTarget: HTMLInputElement }): void => {
        if (changeRequested) return;

        let value = event.currentTarget.value;
        const name = event.currentTarget.name;

        if(value.length > 1 && ObjectHelper.charAt(value, 0) === '.') {
            setters[name]?.(value);
            return;
        }

        if (value.length === 1 && (ObjectHelper.charAt(value, 0) === ',' ||  ObjectHelper.charAt(value, 0) === '.')) {
            value = '0.';
        }

        if (HourHelper.isValidHour(value)) {
            value = HourHelper.getHoursInNumberFormat(value);

            const theEntry = {...entry};
            (theEntry as {[key: string]: any})[name] = value ? parseFloat(value) : 0;
            theEntry.totalHours = theEntry.normalHours + theEntry.extraHours + theEntry.standByHours + theEntry.interventionHours;

            if ((theEntry  as {[key: string]: any})[name] !== (entry  as {[key: string]: any})[name]) {
                setEntry(theEntry);
                onEntryChangeDebounceRef.current(theEntry);
            }

            setters[name]?.(value);
        }
    };

    const onBlur = (event: React.SyntheticEvent<HTMLInputElement> & { currentTarget: HTMLInputElement }): void => {
        // Cancel any pending debounced calls before making immediate call
        onEntryChangeDebounceRef.current.cancel();
        
        onEntryChange(entry);

        let value = event.currentTarget.value;
        const name = event.currentTarget.name;

        if (value.endsWith('.') || value.endsWith(':')) {
            value = value.slice(0, -1);

            setters[name]?.(value);
        }
    };

    const doScrollIntoView = (input: HTMLInputElement): void => {
        if (scrollIntoView) {
            if (input) input.scrollIntoView(false);
            setScrollIntoView(false);
        }
    };

    const getOffset = (el: any) => {
        let x = 0;
        let y = 0;
        while(el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
            x += el.offsetLeft - el.scrollLeft;
            y += el.offsetTop - el.scrollTop;
            el = el.offsetParent;
        }
        return { top: y, left: x };
    };

    const selectActiveInputLevel = (): void => {
        if (props.isActive) {
            switch (props.activeRowLevel) {
                case 1:
                    if(!normalHoursInput) return;
                    if (!isCommentMandatoryOpen() && normalHoursInput) {
                        normalHoursInput.select();
                    }

                    doScrollIntoView(normalHoursInput);
                    if (props.settings.timesheetCodeOrder) {
                        if (normalHoursInput && normalHoursInput.getBoundingClientRect().top + 60 > window.innerHeight) {
                            window.scrollTo(getOffset(normalHoursInput).left, window.pageYOffset + 40);
                        }
                    } else {
                        if (!WindowHelper.isElementInViewport(normalHoursInput)) {
                            window.scrollTo(getOffset(normalHoursInput).left,getOffset(normalHoursInput).top - 999);
                        }
                    }
                    break;
                case 2:
                    if(!normalHoursExpandedInput) return;
                    if (!isCommentMandatoryOpen() && normalHoursExpandedInput) {
                        normalHoursExpandedInput.select();
                    }
                    doScrollIntoView(normalHoursExpandedInput);
                    if (!WindowHelper.isElementInViewport(interventionHoursExpandedInput)) {
                        if (interventionHoursExpandedInput) interventionHoursExpandedInput.scrollIntoView(false);
                        if (props.settings.timesheetCodeOrder) {
                            window.scrollTo(getOffset(interventionHoursExpandedInput).left, window.pageYOffset + 30);
                        } else {
                            window.scrollTo(getOffset(interventionHoursExpandedInput).left,getOffset(interventionHoursExpandedInput).top - 999);
                        }
                    }
                    break;
                case 3:
                    if(!extraHoursExpandedInput) return;
                    if (!isCommentMandatoryOpen() && extraHoursExpandedInput) {
                        extraHoursExpandedInput.select();
                    }
                    doScrollIntoView(extraHoursExpandedInput);
                    if (!WindowHelper.isElementInViewport(extraHoursExpandedInput)) {
                        if (!props.settings.timesheetCodeOrder) {
                            window.scrollTo(getOffset(interventionHoursExpandedInput).left,getOffset(interventionHoursExpandedInput).top - 999);
                        }
                    }
                    break;
                case 4:
                    if(!standByHoursExpandedInput) return;
                    if (!isCommentMandatoryOpen() && standByHoursExpandedInput) {
                        standByHoursExpandedInput.select();
                    }
                    doScrollIntoView(standByHoursExpandedInput);
                    if (!WindowHelper.isElementInViewport(standByHoursExpandedInput)) {
                        if (!props.settings.timesheetCodeOrder) {
                            window.scrollTo(getOffset(interventionHoursExpandedInput).left,getOffset(interventionHoursExpandedInput).top - 999);
                        }
                    }
                    break;
                case 5:
                    if(!interventionHoursExpandedInput) return;
                    if (!isCommentMandatoryOpen() && interventionHoursExpandedInput) {
                        interventionHoursExpandedInput.select();
                    }
                    doScrollIntoView(interventionHoursExpandedInput);
                    if (!WindowHelper.isElementInViewport(interventionHoursExpandedInput)) {
                        if (!props.settings.timesheetCodeOrder) {
                            window.scrollTo(getOffset(interventionHoursExpandedInput).left,getOffset(interventionHoursExpandedInput).top - 999);
                        }
                    }
                    break;
            }
        }
    };

    const getHoursInputFixedClassNames = (): string => {
        let className = '';

        if ((props.isReadOnly && props.entry !== TimesheetHelper.defaultEntry) || props.isCompleted || !props.isTsCodeActive)
            className += ' readonly';

        if (props.isWeekend)
            className += ' weekend';

        if (props.isVacationOrIllness && props.entry !== TimesheetHelper.defaultEntry || !props.isTsCodeActive)
            className += ' vacation';

        return className;
    };

    const getHoursInputFlexibleClassNames = (level: number): string => {
        let className = '';

        if (props.activeRowLevel === level) {
            className += ' active-row-level';

            if (props.isActive)
                className += ' active';
        }

        if (props.multiSelected && level === 1) {
            className += ' multi-selected';

            if (props.multiSelectLeftBorder)
                className += ' left-border';

            if (props.multiSelectRightBorder)
                className += ' right-border';
        }

        if (changeRequested)
            className += ' change-requested';

        return className;
    };

    const getHoursInputClassNames = (level: number): string => {
        let className = 'hours-input';

        className += getHoursInputFixedClassNames();

        if (TimesheetHelper.hasExtraHours(entry) && level === 1)
            className += ' extra';

        className += getHoursInputFlexibleClassNames(level);

        return className;
    };

    const getHours = (): string => {
        switch (props.activeRowLevel) {
            case 1:
            case 2: {
                return normalHours;
            }
            case 3: {
                return extraHours;
            }
            case 4: {
                return standByHours;
            }
            case 5: {
                return interventionHours;
            }
        }
        return '';
    };

    const onSubmit = (hours: string, comments: string | undefined) => {
        const theEntry: StoreTimesheetEntry = {...entry};

        theEntry.comments = comments;

        switch (props.activeRowLevel) {
            case 1:
            case 2: {
                theEntry.normalHours = parseFloat(hours);
                setNormalHours(hours);
                break;
            }
            case 3: {
                theEntry.extraHours = parseFloat(hours);
                setExtraHours(hours);
                break;
            }
            case 4: {
                theEntry.standByHours = parseFloat(hours);
                setStandByHours(hours);
                break;
            }
            case 5: {
                theEntry.interventionHours = parseFloat(hours);
                setInterventionHours(hours);
                break;
            }
        }

        theEntry.totalHours = theEntry.normalHours + theEntry.extraHours + theEntry.standByHours + theEntry.interventionHours;

        onEntryChange(theEntry);
    };

    const isCommentMandatoryOpen = (): boolean => props.isActive && props.commentMandatory && !props.multiSelectEntryPoint;

    const onDayHoursClick = (event: Event & { keepEntryActive?: boolean }): void => {
        event.keepEntryActive = true;
    };

    useEffect(() => { //Did Mount
        (dayHoursContainer as HTMLElement)?.addEventListener('mousedown', onDayHoursClick);
        selectActiveInputLevel();

        return () => { //unmount
            (dayHoursContainer as HTMLElement)?.removeEventListener('mousedown', onDayHoursClick);
            onEntryChange(entry);
        };
        // eslint-disable-next-line @eslint-react/exhaustive-deps -- emuleert componentDidMount/componentWillUnmount en mag enkel bij mount en unmount draaien
    }, []);
    
    const prevPropsRef = useRef<Props>(props);
    useEffect(() => { //Did Update
        if (props.isActive) {
            if (!prevPropsRef.current.isActive || prevPropsRef.current.activeRowLevel !== props.activeRowLevel) {
                if (props.activeRowLevel === 1) {
                    if (entry.normalHours !== entry.totalHours) {
                        if (!props.expanded) {
                            onEntryExpand();
                        } else {
                            props.onNavigateEntry(({type: 'click', key: '', shiftKey: false, altKey: false, ctrlKey: false, preventDefault: () => { return; }}), props.dayIndex, props.day, 2);
                        }
                        return;
                    }
                }
                if((!prevPropsRef.current.setActiveEntryRequested && !props.setActiveEntryRequested) || !commentVisible) {
                    selectActiveInputLevel();
                }
            }
        }

        if(TimesheetHelper.changeInEntries(prevPropsRef.current.entry, props.entry)) {
            setNormalHours(HourHelper.getHoursAsText(props.entry.normalHours, ''));
            setInterventionHours(HourHelper.getHoursAsText(props.entry.interventionHours, ''));
            setExtraHours(HourHelper.getHoursAsText(props.entry.extraHours, ''));
            setStandByHours(HourHelper.getHoursAsText(props.entry.standByHours, ''));
        }
        
        prevPropsRef.current = props;
        // eslint-disable-next-line @eslint-react/exhaustive-deps -- emuleert componentDidUpdate: props is bewust de enige trigger, de overige waarden worden enkel uitgelezen
    }, [props]);

    const onMultiEditSubmit = (hours: string, comments: string | undefined): void => {
        props.onMultiEditSubmit({normalHours: hours, comments: comments  ? comments : ''});
    };

    // Synchroniseert de entry uit de props naar de lokale state zodra de parent een nieuwe versie doorgeeft.
    /* eslint-disable @eslint-react/set-state-in-effect -- de lokale state volgt hier bewust de props */
    useEffect(() => {
        if (!props.isActive && commentVisible) {
            setCommentVisible(false);
        }

        if (props.entry !== entry) {
            setChangeRequested(false);
            setEntry({...props.entry});
        }
        // eslint-disable-next-line @eslint-react/exhaustive-deps -- entry is de waarde waarmee vergeleken wordt, niet de trigger; als dependency zou de effect zichzelf blijven herstarten
    }, [props.isActive, props.entry, commentVisible]);
    /* eslint-enable @eslint-react/set-state-in-effect */

    const setDayHoursRef = (el: HTMLDivElement): void => {dayHoursContainer = el;};
    const setNormalHoursRef = (el: HTMLInputElement): void => {normalHoursInput = el;};
    const setNormalHoursExpandedRef = (el: HTMLInputElement): void => {normalHoursExpandedInput = el;};
    const setExtraHoursExpandedRef = (el: HTMLInputElement): void => {extraHoursExpandedInput = el;};
    const setInterventionHoursExpandedRef = (el: HTMLInputElement): void => {interventionHoursExpandedInput = el;};
    const setStandByHoursExpandedRef = (el: HTMLInputElement): void => {standByHoursExpandedInput = el;};
   
    return (
        <div
            className={'day-hours-container' + (!props.isReadOnly && !props.isCompleted && props.isTsCodeActive ? ' editable' : '')}
            ref={setDayHoursRef}>
            <div className={(props.settings.monthViewSelected ? 'day-hours' : 'day-hours-weekly') +
                    ((props.settings.showCommentTooltips || props.isReadOnly || props.isCompleted) && props.entry.comments ? props.settings.monthViewSelected ? ' tooltip-data-content' : ' tooltip-data-content-weekly' : '')}
                    data-content={entry.comments}>
                <input id={`normalHoursInput-${props.tsCodeId}-${props.day.dayOfMonth}`}
                        ref={setNormalHoursRef}
                        name="normalHours"
                        className={getHoursInputClassNames(1)}
                        value={(!TimesheetHelper.hasExtraHours(entry) ? normalHours : + entry.totalHours.toFixed(2))}
                        onChange={onChange}
                        onClick={onNavigateEntry}
                        onKeyDown={onNavigateEntry}
                        onMouseDown={onNavigateEntry}
                        onMouseEnter={onNavigateEntry}
                        onMouseLeave={onNavigateEntry}
                        onMouseUp={onNavigateEntry}
                        onDoubleClick={onCommentAdd}
                        onBlur={onBlur}
                        readOnly={props.isReadOnly || props.isCompleted || props.commentMandatory || !props.isTsCodeActive}
                        autoComplete="off"/>
                {entry.comments ? <i className="fas fa-circle comment-indicator "/> : null}
            </div>
            {props.isActive && !props.expanded && !props.commentMandatory ?
                <div className={props.settings.monthViewSelected ? 'actions' : 'actions-weekly'}>
                    <div>
                        <i id="comment" className="fas fa-comment add-comment" aria-hidden="true"
                            onClick={onCommentAdd}/>
                        <i className="fas fa-plus add-extra-hours" aria-hidden="true"
                            onClick={onEntryExpand}/>
                    </div>
                </div> : null}
            {props.isActive && commentVisible ?
                <TimesheetCodeDayAddHoursComment
                    commentMandatory={false}
                    comments={entry.comments}
                    level={props.activeRowLevel}
                    getHours={getHours}
                    onSubmit={onSubmit}
                    resetToDefaultEntry={onCommentCancel}/> : null}
            {(isCommentMandatoryOpen()) ?
                <TimesheetCodeDayAddHoursComment
                    commentMandatory
                    comments={entry.comments}
                    level={props.activeRowLevel}
                    getHours={getHours}
                    onSubmit={onSubmit}
                    resetToDefaultEntry={props.resetToDefaultEntry}/> : null}
            {props.multiSelectEntryPoint ?
                <TimesheetCodeDayAddHoursComment
                    commentMandatory={props.commentMandatory}
                    level={props.activeRowLevel}
                    getHours={getHours}
                    onSubmit={onMultiEditSubmit}
                    resetToDefaultEntry={props.onMultiEditCancel}
                    />
                    : null}
            {props.expanded ?
                <div className={props.settings.monthViewSelected ? 'day-hours-extra' : 'day-hours-extra-weekly'}>
                    <input  id={`normalHoursExpandedInput-${props.tsCodeId}-${props.day.dayOfMonth}`}
                            ref={setNormalHoursExpandedRef}
                            name="normalHours"
                            className={getHoursInputClassNames(2)}
                            value={normalHours}
                            onChange={onChange}
                            onClick={onNavigateEntry}
                            onKeyDown={onNavigateEntry}
                            onMouseEnter={onNavigateEntry}
                            onDoubleClick={onCommentAdd}
                            onBlur={onBlur}
                            readOnly={props.isReadOnly || props.isCompleted || props.commentMandatory}
                            autoComplete="off"/>
                    <input id={`extraHoursExpandedInput-${props.tsCodeId}-${props.day.dayOfMonth}`}
                            ref={setExtraHoursExpandedRef}
                            name="extraHours"
                            className={getHoursInputClassNames(3)}
                            value={extraHours}
                            onChange={onChange}
                            onClick={onNavigateEntry}
                            onKeyDown={onNavigateEntry}
                            onMouseEnter={onNavigateEntry}
                            onDoubleClick={onCommentAdd}
                            onBlur={onBlur}
                            readOnly={props.isReadOnly || props.isCompleted || props.commentMandatory}
                            autoComplete="off"/>
                    <input id={`standByHoursExpandedInput-${props.tsCodeId}-${props.day.dayOfMonth}`}
                            ref={setStandByHoursExpandedRef}
                            name="standByHours"
                            className={getHoursInputClassNames(4)}
                            value={standByHours}
                            onChange={onChange}
                            onClick={onNavigateEntry}
                            onKeyDown={onNavigateEntry}
                            onMouseEnter={onNavigateEntry}
                            onDoubleClick={onCommentAdd}
                            onBlur={onBlur}
                            readOnly={props.isReadOnly || props.isCompleted || props.commentMandatory}
                            autoComplete="off"/>
                    <input id={`interventionHoursExpandedInput-${props.tsCodeId}-${props.day.dayOfMonth}`}
                            ref={setInterventionHoursExpandedRef}
                            name="interventionHours"
                            className={getHoursInputClassNames(5)}
                            value={interventionHours}
                            onChange={onChange}
                            onClick={onNavigateEntry}
                            onKeyDown={onNavigateEntry}
                            onMouseEnter={onNavigateEntry}
                            onDoubleClick={onCommentAdd}
                            onBlur={onBlur}
                            readOnly={props.isReadOnly || props.isCompleted || props.commentMandatory}
                            autoComplete="off"/>
                </div> : null}
            {props.expanded ?
                <div
                    className={(props.settings.monthViewSelected ? 'actions-extra' : 'actions-extra-weekly') + getHoursInputFixedClassNames()}>
                    {props.isActive && !props.commentMandatory ?
                        <div><i id="extraComment" className="fas fa-comment add-comment" aria-hidden="true"
                                onClick={onCommentAdd}/></div> : null}
                </div> : null}
        </div>
    );
};
